// @givmo/board — the receipts page's two dialogs (CBG-YR5-DIST's web half, frame p7's quiet acts):
// recording a printed copy handed to someone, and withdrawing the copy.
//
// Each act answers the receipts read, which the page takes as it comes (`useSettleReturnAct`). Every
// refusal is the backend's sentence (the copy withdrawn meanwhile, a printed copy already recorded, a
// day after today or before the copy), shown as served.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, ErrorMessage, Select, TextField, Textarea, toast } from '@givmo/design-system';
import { useBoardOperations } from '../../board-runtime';
import { todayAsDateInput } from '../../lib/display';
import { DialogFrame } from '../conflicts/coi-parts';
import { personName, type ReturnCopy } from './return-decode';
import { servedSentence, useSettleReturnAct } from './return-queries';
import { returnWords as w } from './return-words';

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';

/** "Record a printed copy given": one person, the day it was handed over, and how. */
export function PrintedCopyDialog({
  orgRef,
  copy,
  onClose,
}: {
  orgRef: string;
  copy: ReturnCopy;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const settle = useSettleReturnAct(orgRef);
  const people = copy.people.filter((person) => person.can_record_printed_copy);
  const [personId, setPersonId] = useState<string | null>(
    people.length === 1 ? (people[0]?.board_member_id ?? null) : null,
  );
  const [givenOn, setGivenOn] = useState(todayAsDateInput());
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const chosen = people.find((person) => person.board_member_id === personId) ?? null;

  const record = useMutation({
    mutationFn: async () => {
      if (chosen === null) return null;
      const { data } = await operations.officerRecordBoardReturnPrintedCopy({
        path: {
          org_ref: orgRef,
          record_id: copy.record_id,
          board_member_id: chosen.board_member_id,
        },
        body: { given_on: givenOn, note: note.trim() },
        throwOnError: true,
      });
      return data;
    },
    onMutate: () => setError(null),
    onSuccess: async (data) => {
      await settle(data);
      if (chosen !== null) toast.success(w.printedCopyRecorded(personName(chosen)));
      onClose();
    },
    onError: (caught) => setError(servedSentence(caught, w.printedCopyFailed)),
  });

  return (
    <DialogFrame
      title={w.printedCopyTitle}
      testId="return-printed-copy-dialog"
      width={460}
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="return-printed-copy-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={chosen === null || givenOn.length === 0 || note.trim().length === 0}
            loading={record.isPending}
            onClick={() => record.mutate()}
            testId="return-printed-copy-confirm"
          >
            {w.recordIt}
          </Button>
        </>
      }
    >
      {people.length === 0 ? (
        <p className={SECONDARY} data-testid="return-printed-copy-none">
          {w.noOneForPrintedCopy}
        </p>
      ) : (
        <>
          <Select
            appearance="saas"
            label={w.printedCopyPerson}
            placeholder={w.printedCopyPersonPlaceholder}
            options={people.map((person) => ({
              value: person.board_member_id,
              label: personName(person),
            }))}
            value={personId}
            disabled={record.isPending}
            onValueChange={(value) => setPersonId(value)}
            testId="return-printed-copy-person"
          />
          <TextField
            label={w.printedCopyGivenOn}
            type="date"
            value={givenOn}
            disabled={record.isPending}
            onChange={(event) => setGivenOn(event.target.value)}
            testId="return-printed-copy-day"
          />
          <Textarea
            label={w.printedCopyNote}
            placeholder={w.printedCopyNotePlaceholder}
            value={note}
            maxLength={2000}
            rows={2}
            disabled={record.isPending}
            onChange={(event) => setNote(event.target.value)}
            testId="return-printed-copy-note"
          />
        </>
      )}
      {error !== null ? (
        <ErrorMessage testId="return-printed-copy-error">{error}</ErrorMessage>
      ) : null}
    </DialogFrame>
  );
}

/** "Withdraw this copy": the reason is required; the record, the receipts and the file are kept. */
export function WithdrawCopyDialog({
  orgRef,
  copy,
  onClose,
}: {
  orgRef: string;
  copy: ReturnCopy;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const settle = useSettleReturnAct(orgRef);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const withdraw = useMutation({
    mutationFn: async () => {
      const { data } = await operations.officerWithdrawBoardReturn({
        path: { org_ref: orgRef, record_id: copy.record_id },
        body: { reason: reason.trim() },
        throwOnError: true,
      });
      return data;
    },
    onMutate: () => setError(null),
    onSuccess: async (data) => {
      await settle(data);
      toast.success(w.withdrawn);
      onClose();
    },
    onError: (caught) => setError(servedSentence(caught, w.withdrawFailed)),
  });

  return (
    <DialogFrame
      title={w.withdrawTitle}
      testId="return-withdraw-dialog"
      width={440}
      onClose={onClose}
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={onClose}
            testId="return-withdraw-cancel"
          >
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={reason.trim().length === 0}
            loading={withdraw.isPending}
            onClick={() => withdraw.mutate()}
            testId="return-withdraw-confirm"
          >
            {w.withdrawButton}
          </Button>
        </>
      }
    >
      <p className={SECONDARY}>{w.withdrawLead}</p>
      <Textarea
        label={w.withdrawReason}
        value={reason}
        maxLength={2000}
        rows={2}
        disabled={withdraw.isPending}
        onChange={(event) => setReason(event.target.value)}
        testId="return-withdraw-reason"
      />
      {error !== null ? <ErrorMessage testId="return-withdraw-error">{error}</ErrorMessage> : null}
    </DialogFrame>
  );
}
